<script setup lang="ts" generic="T">
import { QItem, QSelect, QVirtualScroll } from 'quasar'
import { computed, onMounted, ref, useSlots, watch } from 'vue'
import { Color } from '@previo/codebooks'
import type { PValidationRule } from '../../types/types'
import { $t } from '../../composables/i18n'
import { useHandleScrollQSelect } from '../../composables/menu/handleScrollQSelect'
import { useMenuMaxHeight } from '../../composables/menu/useMenuMaxHeight'
import PCard from '../card/PCard.vue'
import { PCardSize } from '../card/types'
import PIcon from '../icon/PIcon.vue'
import PTextFormBody from '../typography/form/PTextFormBody.vue'
import { usePrevioDS } from '../../composables/helpers'
import { PMaxLines } from '../typography/types'
import InputFieldLabel from './components/LabelInputField.vue'
import CreateBtn from './components/CreateBtn.vue'
import MobileSuggestionDialog from './components/MobileSuggestionDialog.vue'
import { menuOffset } from './composable/menuOffset'
import PCheckbox from './PCheckbox.vue'
import PTextInputField from './PTextInputField.vue'
import Chip from './components/Chip.vue'
import { PInputAutofocusType } from './types'

type Props = {
  items: T[]
  itemValue: keyof T
  itemText: ((item: T) => string) | keyof T
  tooltip?: null | string
  label?: null | string
  placeholder?: null | string
  hint?: string
  rules?: PValidationRule[]
  loading?: boolean
  noData?: null | string
  disable?: boolean
  readonly?: boolean
  debounce?: null | number
  disableClearable?: boolean
  itemDisabled?: (item: T) => boolean
  errorMessage?: null | string
  create?: null | string
  isCreateVisible?: boolean
  createEndIcon?: null | string
  autofocus?: boolean
  autofocusType?: null | PInputAutofocusType
  defaultFilter?: boolean
  cy: string
}

type Emits = {
  (e: 'filter', val: null | string): void
  (e: 'create', val: null | string): void
  (e: 'blur'): void
}

const model = defineModel<(string | number)[]>({ default: [] })

const quasarModel = computed<(string | number)[] | null>({
  get() {
    return model.value
  },
  set(value) {
    // Quasar sets the model to null on clear
    model.value = value ?? []
  },
})

const props = withDefaults(defineProps<Props>(), {
  disable: false,
  readonly: false,
  disableClearable: false,
  isCreateVisible: true,
  loading: false,
  autofocus: false,
  autofocusType: PInputAutofocusType.INPUT,
  defaultFilter: false,
  rules() {
    return []
  },
})

const emits = defineEmits<Emits>()

const ds = usePrevioDS()

const slots = useSlots()

const qSelectComponent = ref<InstanceType<typeof QSelect>>()
const handleScrollQSelect = useHandleScrollQSelect(qSelectComponent)
const menuMaxHeight = useMenuMaxHeight(() => qSelectComponent.value?.$el as HTMLElement | undefined)

const inputValue = ref<null | string>(null)
const isMobileDialogShow = ref<boolean>(false)

// Hack: Quasar emit `@popup-show` on QSelect after call `removeAtIndex` function in `#selected-item` slot. This is solution how to prevent it
const isOpenMobileDialogIgnored = ref<boolean>(false)

const inputDebounce = computed<number>(() => {
  if (props.debounce === undefined) {
    // no explicit debounce given - use 0 if data can be assumed to be local
    return props.defaultFilter ? 0 : ds.debounce.API_DEBOUNCE
  }

  return props.debounce ?? 0
})

const itemsFiltered = computed<T[]>(() => {
  if (props.defaultFilter && inputValue.value !== null && inputValue.value !== '') {
    const needle = inputValue.value.toLowerCase()

    return props.items.filter((item) => itemLabel(item)?.toLowerCase().indexOf(needle) > -1)
  }

  return props.items
})

const isRequired = computed(() => {
  return props.rules.includes(ds.rules.required)
})

const hasAnyErrors = computed<boolean>(() => {
  return !!props.errorMessage
})

const isCreateBtnVisible = computed<boolean>(() => {
  return props.isCreateVisible && !!props.create
})

const isSelected = (item: T): boolean => {
  return model.value.includes(item[props.itemValue] as string | number)
}

const isItemDisabled = (item: T): boolean => {
  return props.itemDisabled?.(item) ?? false
}

const selectItem = (item: T): void => {
  if (isItemDisabled(item)) {
    return
  }

  qSelectComponent.value?.toggleOption(item[props.itemValue])
  qSelectComponent.value?.updateInputValue('')
}

const itemLabel = (item: T): string => {
  return typeof props.itemText === 'function'
    ? props.itemText(item)
    : (item[props.itemText] as string)
}

const optionLabel = computed<string | ((item: T) => string)>(() => {
  return typeof props.itemText === 'function' ? props.itemText : (props.itemText as string)
})

const isModelEmpty = computed<boolean>(() => {
  return model.value.length === 0
})

const changeInputValue = (value: string | number | null | undefined): void => {
  if (value == null || value === '') {
    value = null
  } else {
    value = value.toString()
  }

  inputValue.value = value

  if (ds.screen().isLaptopAndLarge) {
    qSelectComponent.value?.showPopup()
  }

  emits('filter', value)
}

const onCreate = (value: string | null): void => {
  if (!isCreateBtnVisible.value) {
    return
  }

  emits('create', value)
}

watch(model, () => {
  qSelectComponent.value?.updateInputValue('')
})

const onMenuShow = (): void => {
  if (ds.screen().isLaptopAndLarge) {
    handleScrollQSelect.subscribe()
    menuMaxHeight.subscribe()

    return
  }

  qSelectComponent.value?.hidePopup()

  if (isOpenMobileDialogIgnored.value) {
    isOpenMobileDialogIgnored.value = false
  } else {
    isMobileDialogShow.value = true
  }
}

const onMenuHide = (): void => {
  if (ds.screen().isLaptopAndLarge) {
    handleScrollQSelect.unsubscribe()
    menuMaxHeight.unsubscribe()
  }
}

const removeChip = (index: number, removeAtIndex: (index: number) => void): void => {
  if (props.readonly) {
    return
  }

  isOpenMobileDialogIgnored.value = true
  removeAtIndex(index)
}

const showPopup = (): void => {
  if (ds.screen().isMobile) {
    isMobileDialogShow.value = true

    return
  }

  qSelectComponent.value?.showPopup()
}

onMounted(() => {
  if (props.autofocus && props.autofocusType === PInputAutofocusType.DROPDOWN) {
    showPopup()
  }
})
</script>

<template>
  <input-field-label :label="label" :is-required="isRequired" :tooltip="tooltip" />

  <q-select
    ref="qSelectComponent"
    v-model="quasarModel"
    behavior="menu"
    popup-no-route-dismiss
    :options="itemsFiltered"
    :option-label="optionLabel"
    :option-value="itemValue as string"
    :option-disable="isItemDisabled"
    options-cover
    map-options
    :emit-value="true"
    :rules="rules"
    :loading="loading"
    :hint="hint"
    :error="hasAnyErrors"
    :placeholder="ds.screen().isLaptopAndLarge && isModelEmpty ? placeholder : undefined"
    :disable="disable"
    :readonly="readonly"
    :class="{ 'ds-input-field--readonly': readonly }"
    :bg-color="Color.WHITE"
    :error-message="errorMessage ?? undefined"
    :autofocus="autofocus"
    :data-cy="cy"
    :input-debounce="inputDebounce"
    :use-input="ds.screen().isLaptopAndLarge"
    :menu-offset="menuOffset"
    :popup-content-class="menuMaxHeight.popupContentClass.value"
    :popup-content-style="menuMaxHeight.popupContentStyle.value"
    autocomplete="nope"
    multiple
    clear-icon="fa-light fa-xmark"
    :clearable="!disableClearable"
    outlined
    use-chips
    hide-dropdown-icon
    hide-bottom-space
    no-error-icon
    @new-value="onCreate"
    @input-value="changeInputValue"
    @popup-show="onMenuShow"
    @popup-hide="onMenuHide"
  >
    <template v-if="ds.screen().isMobile && isModelEmpty" #prepend>
      <p-text-form-body :text="placeholder" :color="Color.GREY_400" :max-lines="PMaxLines.ONE" />
    </template>

    <template v-if="slots['options-header'] || isCreateBtnVisible" #before-options>
      <slot name="options-header" />
      <div v-if="isCreateBtnVisible" class="p-ma-sm">
        <create-btn :text="create" :end-icon="createEndIcon" @click="onCreate(inputValue)" />
      </div>
    </template>

    <template
      #selected-item="{
        index,
        opt,
        removeAtIndex,
      }: {
        index: number
        opt: T
        removeAtIndex(index: number): void
      }"
    >
      <slot
        name="selected-item-chips"
        :item="opt"
        :readonly="readonly"
        :remove-item="
          () => {
            removeChip(index, removeAtIndex)
          }
        "
      >
        <chip
          :label="itemLabel(opt)"
          :hide-remove="readonly"
          @remove="
            () => {
              removeChip(index, removeAtIndex)
            }
          "
        />
      </slot>
    </template>

    <template
      #option="{ itemProps, opt, selected }: { itemProps: object; opt: T; selected: boolean }"
    >
      <q-item v-bind="itemProps" :disable="isItemDisabled(opt)">
        <div class="p-flex p-items-center p-flex-1">
          <p-checkbox
            :model-value="selected"
            :cy="`item_${opt[itemValue as keyof T]}`"
            @update:model-value="selectItem(opt)"
          />
          <template v-if="!!slots['item']">
            <slot name="item" :item="opt" :is-selected="selected" />
          </template>

          <template v-else>
            <p-text-form-body
              :text="itemLabel(opt)"
              :color="selected ? Color.PRIMARY : Color.GREY_600"
              :max-lines="PMaxLines.ONE"
            />
          </template>
        </div>
      </q-item>
    </template>

    <template #no-option>
      <div v-if="isCreateBtnVisible" class="p-ma-sm">
        <create-btn :text="create" :end-icon="createEndIcon" @click="onCreate(inputValue)" />
      </div>
      <q-item>
        <div class="p-flex p-flex-center">
          <p-text-form-body :text="noData || $t('No items match the value')" />
        </div>
      </q-item>
    </template>

    <template v-if="!loading" #append>
      <p-icon icon="search" />
    </template>
  </q-select>

  <mobile-suggestion-dialog v-model="isMobileDialogShow" show-confirm-buttons>
    <template #input>
      <p-text-input-field
        v-model="inputValue"
        end-icon="magnifying-glass"
        :placeholder="$t('Search')"
        :debounce="inputDebounce"
        cy="search-input"
        @update:model-value="changeInputValue"
      />
    </template>
    <template #default>
      <slot name="options-header" />
      <q-virtual-scroll :items="itemsFiltered" separator class="p-flex-1">
        <template v-if="isCreateBtnVisible" #before>
          <p-card :pa-mobile="PCardSize.SM" class="p-flex p-justify-center p-items-center">
            <create-btn
              v-if="isCreateBtnVisible"
              :text="create"
              :end-icon="createEndIcon"
              @click="onCreate(inputValue)"
            />

            <q-item v-if="itemsFiltered.length === 0">
              <div class="p-flex p-flex-center">
                <p-text-form-body :text="noData || $t('No items match the value')" />
              </div>
            </q-item>
          </p-card>
        </template>

        <template #default="{ item }">
          <q-item clickable :disable="isItemDisabled(item)" @click="selectItem(item)">
            <div class="p-flex p-items-center p-flex-1">
              <p-checkbox
                :model-value="isSelected(item)"
                :cy="`item-${item[itemValue]}`"
                @update:model-value="selectItem(item)"
              />

              <template v-if="!!slots['item']">
                <slot name="item" :item="item" :is-selected="isSelected(item)" />
              </template>

              <template v-else>
                <p-text-form-body
                  :text="itemLabel(item)"
                  :color="isSelected(item) ? Color.PRIMARY : Color.GREY_600"
                  :max-lines="PMaxLines.ONE"
                />
              </template>
            </div>
          </q-item>
        </template>
      </q-virtual-scroll>
    </template>
  </mobile-suggestion-dialog>
</template>
